import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { SnapTradeReact } from "snaptrade-react";
import apiClient from "../api/client";
import { connectAccount, setupAccount } from "../api/accounts";
import { createProfile, updateProfile, deleteProfile as apiDeleteProfile } from "../api/profile";
import "../css/Modal.css";
import TaxRateData from "../tax_assets/us_tax_data.json";
import "../css/Form.css";

export default function Modal({ isOpen, onClose, username, setIsOnboardingProcessing }) {
  const [isProfileInfoComplete, setIsProfileInfoComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loginLink, setLoginLink] = useState("");
  const [accountLinked, setAccountLinked] = useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);

  const [profileFormData, setProfileFormData] = useState({
    country: "US",
    filingStatus: "",
    state: "",
    incomeBracketFederalShortTermTaxRate: "",
    incomeBracketFederalLongTermTaxRate: "",
    federalLongTermTaxRate: 0,
    federalShortTermTaxRate: 0,
    incomeBracketStateTaxRate: "",
    stateTaxRate: 0,
    totalLongTermTaxRate: 0,
    totalShortTermTaxRate: 0,
  });

  useEffect(() => {
    if (isOpen.open) {
      const initializeModal = async () => {
        // If modal type is "profile", show profile form directly
        if (isOpen.type === "profile") {
          try {
            const profileResponse = await apiClient.get('/api/v1/profile');
            console.log("Profile exists:", profileResponse);
            setIsProfileInfoComplete(true);
            setShowProfileForm(true); // Show profile form for editing
            // TODO: Load existing profile data into form
          } catch (err) {
            console.log("Profile check error:", err);
            if (err.response?.status === 404) {
              // Profile doesn't exist, show profile form for creation
              setIsProfileInfoComplete(false);
              setShowProfileForm(true);
            }
          }
          return; // Exit early for profile type
        }

        // For account linking flow (default behavior)
        // If a loginLink was passed (e.g. from reconnect flow), use it and show SnapTrade immediately
        if (isOpen.loginLink && /^https?:\/\//.test(isOpen.loginLink)) {
          setLoginLink(isOpen.loginLink);
          return;
        }
        try {
          // First, check if user profile exists
          const profileResponse = await apiClient.get('/api/v1/profile');
          console.log("Profile exists:", profileResponse);
          setIsProfileInfoComplete(true);
          
          // If profile exists, we don't need to show profile form
          // Just proceed to account linking if needed
          const accountData = await connectAccount();
          setLoginLink(accountData.redirectUrl);
        } catch (err) {
          console.log("Profile check error:", err);
          if (err.response?.status === 404) {
            // Profile doesn't exist, we'll need to show profile form after account linking
            setIsProfileInfoComplete(false);
            
            // Still create account link for SnapTrade
            try {
              const accountData2 = await connectAccount();
              setLoginLink(accountData2.redirectUrl);
            } catch (accountErr) {
              console.error("Error creating account:", accountErr);
            }
          }
        }
      };
      initializeModal();
    } else {
      setLoginLink("");
      setAccountLinked(false);
      setShowProfileForm(false);
    }
  }, [isOpen, username]);

  const deleteProfile = async () => {
    try {
      const response = await apiDeleteProfile();
      console.log(response);
    } catch (err) {
      console.log("Error deleting profile:", err);
    }
  };

  const submitTaxRateInfo = async () => {
    try {
      const response = await createProfile({
        name: username,
        email: username,
        filingStatus: profileFormData.filingStatus,
        state: profileFormData.state,

        incomeBracketFederalShortTermTaxRate:
          profileFormData.incomeBracketFederalShortTermTaxRate,
        federalShortTermTaxRate:
          Number(profileFormData.federalShortTermTaxRate) * 100,

        incomeBracketFederalLongTermTaxRate:
          profileFormData.incomeBracketFederalLongTermTaxRate,
        federalLongTermTaxRate:
          Number(profileFormData.federalLongTermTaxRate) * 100,

        incomeBracketStateTaxRate: profileFormData.incomeBracketStateTaxRate,
        stateTaxRate: Number(profileFormData.stateTaxRate) * 100,

        totalShortTermTaxRate:
          (Number(profileFormData.federalShortTermTaxRate) +
            Number(profileFormData.stateTaxRate)) *
          100,

        totalLongTermTaxRate:
          (Number(profileFormData.federalLongTermTaxRate) +
            Number(profileFormData.stateTaxRate)) *
          100,
      });

      console.log(response);
      if (isOpen.type === "profile") {
        onClose();
        window.location.reload();
      } else {
        // Profile submitted successfully, proceed with onboarding
        setIsProfileInfoComplete(true);
        if (setIsOnboardingProcessing) setIsOnboardingProcessing(true);
        onClose();
      }
    } catch (err) {
      console.error("Error submitting tax rate info:", err);
    }
  };

  const handleSuccess = async (brokerageAuthorizationId) => {
    setLoginLink("");
    setLoading(true);
    try {
      console.log("Authorization ID:", brokerageAuthorizationId);
      const data = await setupAccount(brokerageAuthorizationId);
      setAccountLinked(true);
      if (data === "Account already exists") {
        localStorage.setItem('snaptrade_connection_status', JSON.stringify({ type: 'warning', message: 'Account already exists.' }));
        window.dispatchEvent(new CustomEvent('snaptradeConnectionComplete', { detail: { type: 'warning', message: 'Account already exists.' } }));
      } else if (data === "Username/brokerage auth is empty or null") {
        localStorage.setItem('snaptrade_connection_status', JSON.stringify({ type: 'error', message: 'Invalid user. Please sign-in again.' }));
        window.dispatchEvent(new CustomEvent('snaptradeConnectionComplete', { detail: { type: 'error', message: 'Invalid user. Please sign-in again.' } }));
      } else {
        localStorage.setItem('snaptrade_connection_status', JSON.stringify({ type: 'success', message: 'Connection successful! We will process the data on the backend side.' }));
        window.dispatchEvent(new CustomEvent('snaptradeConnectionComplete', { detail: { type: 'success', message: 'Connection successful! We will process the data on the backend side.' } }));
      }

      if (!isProfileInfoComplete) {
        setShowProfileForm(true);
      } else {
        if (setIsOnboardingProcessing) setIsOnboardingProcessing(true);
        onClose();
      }
    } catch (err) {
      console.error("Error fetching accounts:", err);
      localStorage.setItem('snaptrade_connection_status', JSON.stringify({ type: 'error', message: 'Connection failed. Please try to reconnect the account. We will process the data on the backend side.' }));
      window.dispatchEvent(new CustomEvent('snaptradeConnectionComplete', { detail: { type: 'error', message: 'Connection failed. Please try to reconnect the account. We will process the data on the backend side.' } }));
      setError("Failed to load accounts.");
    } finally {
      setLoading(false);
    }
  };

  const handleError = async (error) => {
    console.log("Clean up account for user:", username);
    console.log("Error:", error);

    try {
      await apiClient.post('/api/v1/accounts/cleanup', { reason: JSON.stringify(error) });

      localStorage.setItem('snaptrade_connection_status', JSON.stringify({ type: 'error', message: 'Connection failed. Please try again. We will process the data on the backend side.' }));
      window.dispatchEvent(new CustomEvent('snaptradeConnectionComplete', { detail: { type: 'error', message: 'Connection failed. Please try again. We will process the data on the backend side.' } }));
      setAccountLinked(false);
      setShowProfileForm(false);
    } catch (e) {
      console.error("Error cleaning up account:", e);
    }
  };

  const handleExit = async () => {
    setAccountLinked(false);
    setShowProfileForm(false);
    // Dispatch custom event to notify Dashboard to reopen AccountConnectionModal
    window.dispatchEvent(new CustomEvent('snaptradeExited', { detail: { shouldReopenConnectionModal: true } }));
    // Also call onClose to close the modal
    if (onClose) {
      onClose();
    }
  };

  const renderFederalShortTermTaxBrackets = useMemo(() => {
    return TaxRateData.federal.short_term?.[profileFormData.filingStatus]?.map(
      (range) => (
        <option
          key={`federal_short_term_${profileFormData.filingStatus}_${range.max}`}
          value={`${range.min}-${range.max}|${range.rate}`}
        >
          {range.max === "Infinity"
            ? `Greater than $${range.min}`
            : `$${range.min} - $${range.max}`}
          &nbsp;({(range.rate * 100).toFixed(2)}%)
        </option>
      )
    );
  }, [profileFormData.filingStatus]);

  const renderFederalLongTermTaxBrackets = useMemo(() => {
    return TaxRateData.federal.long_term?.[profileFormData.filingStatus]?.map(
      (range) => (
        <option
          key={`federal_long_term_${profileFormData.filingStatus}_${range.max}`}
          value={`${range.min}-${range.max}|${range.rate}`}
        >
          {range.max === "Infinity"
            ? `Greater than $${range.min}`
            : `$${range.min} - $${range.max}`}
          &nbsp;({(range.rate * 100).toFixed(2)}%)
        </option>
      )
    );
  }, [profileFormData.filingStatus]);

  const renderStateTaxBrackets = useMemo(() => {
    return (
      TaxRateData.states[profileFormData.state]?.[
        profileFormData.filingStatus
      ]?.map((range) => (
        <option
          key={`${profileFormData.state}_${profileFormData.filingStatus}_${range.min}-${range.max}`} // Unique key
          value={`${range.min}-${range.max}|${range.rate}`} // Store both income range and rate
        >
          {range.max === "Infinity"
            ? `Greater than $${range.min}`
            : `$${range.min} - $${range.max}`}
          &nbsp;({(range.rate * 100).toFixed(2)}%)
        </option>
      )) || []
    ); // Return empty array if no data exists to prevent errors
  }, [profileFormData.filingStatus, profileFormData.state]);

  console.log(isOpen, !isProfileInfoComplete && loginLink);

  if (!isOpen.open) return null;
  return createPortal(
    <div className="modal">
      {/* Show SnapTrade first if we have a login link and haven't linked account yet */}
      {loginLink && !accountLinked && /^https?:\/\//.test(loginLink) && (
        <SnapTradeReact
          loginLink={loginLink}
          isOpen={loginLink && !accountLinked}
          onSuccess={handleSuccess}
          onError={handleError}
          onExit={handleExit}
          close={onClose}
        />
      )}
      
      {/* Show profile form after successful account linking if profile is needed, or for profile type */}
      {showProfileForm && (isOpen.type === "profile" || !isProfileInfoComplete) && (
        <div className="modal-container">
          <div className="modal-body">
            {/* Show account linking success message only if not profile type */}
            {isOpen.type !== "profile" && (
              <div className="info-box" style={{
                background: '#e8f5e8',
                borderLeft: '4px solid #4caf50',
                padding: '1em',
                borderRadius: '6px',
                marginBottom: '1.5em',
                color: '#234',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75em',
              }}>
                <span style={{fontSize: '1.5em', color: '#4caf50', lineHeight: 1}}>✅</span>
                <div>
                  <strong>Account successfully linked!</strong>
                  <div style={{marginTop: '0.25em'}}>
                    While we sync your accounts and prepare your personalized tax insights, let's take a moment to fill out your tax information. This will help us provide you with the most accurate and personalized recommendations.
                  </div>
                </div>
              </div>
            )}
            
            <div className="info-box" style={{
              background: '#f3f7fa',
              borderLeft: '4px solid #2196f3',
              padding: '1em',
              borderRadius: '6px',
              marginBottom: '1.5em',
              color: '#234',
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75em',
            }}>
              <span style={{fontSize: '1.5em', color: '#2196f3', lineHeight: 1}}>ℹ️</span>
              <div>
                <strong>Why do we ask for your tax bracket?</strong>
                <div style={{marginTop: '0.25em'}}>
                  To give you the most accurate tax insights, we ask for your tax bracket—not your actual income. This helps us calculate your tax implications precisely, so you can make smarter investment decisions.<br/>
                  <strong>Your privacy is important to us:</strong> We never ask for your actual income, and your information is kept secure and confidential.
                </div>
              </div>
            </div>
            <section>
              <h1>
                <svg
                  id="Layer_1"
                  data-name="Layer 1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 94.16 122.88"
                  className="tax-form-icon"
                >
                  <defs></defs>
                  <title>financial-statement</title>
                  <path
                    className="cls-1"
                    d="M35,108l-.35,0H6.1a6.12,6.12,0,0,1-4.3-1.8,6,6,0,0,1-1.8-4.3V6.1A6,6,0,0,1,1.8,1.8,6,6,0,0,1,6.1,0H80.57a6.12,6.12,0,0,1,4.29,1.8,6,6,0,0,1,1.81,4.3V63.76L82.33,58.6a5.86,5.86,0,0,0-.82-.83V42.24H5.24v59.85a.81.81,0,0,0,.26.6.77.77,0,0,0,.6.25l26.71-.08a6.17,6.17,0,0,0,1.45,4.21L35,108Zm58.91-16.4L55.63,122.88,39,103.09l2.27-2.48L53.48,115l.15.19a3.24,3.24,0,0,0,4.57.35l1.25-1.08,0,0,5.91-5.16,26-20.72,2.51,3Zm.22-9.3-38.31,31.3L39.21,93.8,77.52,62.51,94.16,82.29ZM71.82,87.14a5.88,5.88,0,1,1-7-4.44,5.88,5.88,0,0,1,7,4.44Zm13.44-2.5L59.71,105.19a3.85,3.85,0,0,0-5.39-.46l-6.58-7.82a3.81,3.81,0,0,0,.46-5.37L73.76,71a3.83,3.83,0,0,0,5.38.46l6.59,7.82a3.83,3.83,0,0,0-.47,5.39ZM70.61,50.8v3.71a.27.27,0,0,1-.28.29H49a.27.27,0,0,1-.28-.29V50.8a.27.27,0,0,1,.28-.29H70.33a.27.27,0,0,1,.28.29Zm-8,9.49V64a.27.27,0,0,1-.29.28H49.05a.27.27,0,0,1-.29-.28V60.29a.27.27,0,0,1,.29-.28h13.3a.31.31,0,0,1,.29.28Zm-34.54-46h5.48a.33.33,0,0,1,.35.35V33.78a.33.33,0,0,1-.35.34H28.1a.32.32,0,0,1-.34-.34V14.64a.32.32,0,0,1,.34-.35Zm12.49,7.28h5.48a.32.32,0,0,1,.35.34v11.8a.33.33,0,0,1-.35.34H40.59a.33.33,0,0,1-.34-.34V21.91a.36.36,0,0,1,.34-.34Zm25,0h5.48a.32.32,0,0,1,.34.34v11.8a.33.33,0,0,1-.34.34H65.59a.33.33,0,0,1-.35-.34V21.91a.37.37,0,0,1,.35-.34Zm-12.5-7.28h5.48a.33.33,0,0,1,.35.35V33.78a.33.33,0,0,1-.35.34H53.09a.33.33,0,0,1-.35-.34V14.64a.33.33,0,0,1,.35-.35ZM15.62,16.92h5.47a.33.33,0,0,1,.35.35V33.78a.33.33,0,0,1-.35.34H15.62a.33.33,0,0,1-.35-.34V17.27c-.07-.21.14-.35.35-.35ZM28.3,66.26l11.63.21a12.42,12.42,0,0,1-5.47,10.32L28.3,66.26Zm-.64-3.56-.14-13.54v-.89l.9.07h0a16.32,16.32,0,0,1,3.21.55,15.36,15.36,0,0,1,2.94,1.16,15.57,15.57,0,0,1,8.28,13l.07.89H42l-13.48-.41H27.8l-.14-.82Zm1.65-.82,11.83.34a14,14,0,0,0-7.32-10.8,14.92,14.92,0,0,0-3.94-1.34c-.24,0-.47-.08-.71-.1l.14,11.9ZM24.4,65.05,31,76.54a13.43,13.43,0,0,1-6.64,1.78,13.28,13.28,0,0,1-.55-26.54l.55,13.27Zm57.11-27.2V6.1a.77.77,0,0,0-.25-.6.81.81,0,0,0-.6-.26H6.1a.78.78,0,0,0-.6.26.66.66,0,0,0-.26.6V37.85Z"
                  />
                </svg>
                {isOpen.type === "profile" ? "Let's begin with some information first." : "Let's get your tax information for personalized insights."}
              </h1>


              <div className="nice-form-group">
                <label>Country</label>
                <select
                  value={profileFormData.country}
                  onChange={(e) =>
                    setProfileFormData({
                      ...profileFormData,
                      country: e.target.value,
                    })
                  }
                >
                  <option value="US">US</option>
                </select>
              </div>

              <div className="nice-form-group">
                <label>Filing Status</label>
                <select
                  value={profileFormData.filingStatus}
                  onChange={(e) =>
                    setProfileFormData({
                      ...profileFormData,
                      federalLongTermTaxRate: 0,
                      federalShortTermTaxRate: 0,
                      stateTaxRate: 0,
                      filingStatus: e.target.value,
                    })
                  }
                >
                  <option disabled value="">
                    {" "}
                    -- select an option --{" "}
                  </option>
                  <option value="single">Single</option>
                  <option value="married_filing_jointly">
                    Married jointly
                  </option>
                  <option value="married_filing_separately">
                    Married separately
                  </option>
                  <option value="head_of_household">Head of household</option>
                </select>
              </div>

              <div className="nice-form-group">
                <label>Income Bracket for Short Term Federal Tax Rate</label>
                <select
                  value={
                    profileFormData.incomeBracketFederalShortTermTaxRate
                      ? `${profileFormData.incomeBracketFederalShortTermTaxRate}|${profileFormData.federalShortTermTaxRate}`
                      : ""
                  }
                  onChange={(e) => {
                    const [incomeBracket, taxRate] = e.target.value.split("|");
                    setProfileFormData({
                      ...profileFormData,
                      incomeBracketFederalShortTermTaxRate: incomeBracket,
                      federalShortTermTaxRate: parseFloat(taxRate),
                    });
                  }}
                >
                  <option value=""> -- select an option -- </option>
                  {renderFederalShortTermTaxBrackets}
                </select>
              </div>

              <div className="nice-form-group">
                <label>Income Bracket for Long Term Federal Tax Rate</label>
                <select
                  value={
                    profileFormData.incomeBracketFederalLongTermTaxRate
                      ? `${profileFormData.incomeBracketFederalLongTermTaxRate}|${profileFormData.federalLongTermTaxRate}`
                      : ""
                  }
                  onChange={(e) => {
                    const [incomeBracket, taxRate] = e.target.value.split("|");
                    setProfileFormData({
                      ...profileFormData,
                      incomeBracketFederalLongTermTaxRate: incomeBracket,
                      federalLongTermTaxRate: parseFloat(taxRate),
                    });
                  }}
                >
                  <option value=""> -- select an option -- </option>
                  {renderFederalLongTermTaxBrackets}
                </select>
              </div>

              <div className="nice-form-group">
                <p>
                  Your calculated long term federal tax rate:{" "}
                  {profileFormData.federalLongTermTaxRate * 100}%
                </p>
              </div>

              <div className="nice-form-group">
                <p>
                  Your calculated short term federal tax rate:{" "}
                  {profileFormData.federalShortTermTaxRate * 100}%
                </p>
              </div>

              <div className="nice-form-group">
                <label>State</label>
                <select
                  value={profileFormData.state}
                  onChange={(e) =>
                    setProfileFormData({
                      ...profileFormData,
                      stateTaxRate: 0,
                      state: e.target.value,
                    })
                  }
                >
                  <option disabled value="">
                    {" "}
                    -- select an option --{" "}
                  </option>
                  {Object.keys(TaxRateData.states)
                    .sort()
                    .map((state) => {
                      return (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      );
                    })}
                </select>
              </div>

              <div className="nice-form-group">
                <label>Income Bracket for State Tax Rate</label>
                <select
                  value={
                    profileFormData.incomeBracketStateTaxRate
                      ? `${profileFormData.incomeBracketStateTaxRate}|${profileFormData.stateTaxRate}`
                      : ""
                  } // Set to empty string for default option
                  onChange={(e) => {
                    const [incomeBracket, taxRate] = e.target.value.split("|");
                    setProfileFormData({
                      ...profileFormData,
                      incomeBracketStateTaxRate: incomeBracket,
                      stateTaxRate: parseFloat(taxRate),
                    });
                  }}
                >
                  <option value=""> -- select an option -- </option>{" "}
                  {/* Ensure this option exists */}
                  {renderStateTaxBrackets}
                </select>
              </div>

              <div className="nice-form-group">
                <p>
                  Your calculated state tax rate:{" "}
                  {profileFormData.stateTaxRate * 100}%
                </p>
              </div>

              <div className="nice-form-group">
                <p>
                  Your calculated total effective long term tax rate:{" "}
                  {profileFormData.federalLongTermTaxRate * 100 +
                    profileFormData.stateTaxRate * 100}
                  %
                </p>
              </div>
              <div className="nice-form-group">
                <p>
                  Your calculated total effective short term tax rate:{" "}
                  {profileFormData.federalShortTermTaxRate * 100 +
                    profileFormData.stateTaxRate * 100}
                  %
                </p>
              </div>
              <br />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center', width: '100%', marginTop: '2rem' }}>
                <button
                  onClick={submitTaxRateInfo}
                  className="cta-button"
                  style={{ minWidth: '120px', maxWidth: '200px' }}
                >
                  {isOpen.type === "profile" ? "Save Profile" : "Complete Setup"}
                </button>
                {/* Only show skip button for account linking flow, not for profile type */}
                {isOpen.type !== "profile" && (
                  <button
                    onClick={() => {
                      if (setIsOnboardingProcessing) setIsOnboardingProcessing(true);
                      onClose();
                    }}
                    style={{ 
                      background: 'transparent', 
                      border: '1px solid #ccc', 
                      color: '#666',
                      padding: '10px 20px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Skip for now
                  </button>
                )}
              </div>
            </section>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              x="0px"
              y="0px"
              width="100"
              height="100"
              viewBox="0 0 50 50"
            >
              <path d="M 7.7070312 6.2929688 L 6.2929688 7.7070312 L 23.585938 25 L 6.2929688 42.292969 L 7.7070312 43.707031 L 25 26.414062 L 42.292969 43.707031 L 43.707031 42.292969 L 26.414062 25 L 43.707031 7.7070312 L 42.292969 6.2929688 L 25 23.585938 L 7.7070312 6.2929688 z"></path>
            </svg>
          </button>
        </div>
      )}
    </div>,
    document.getElementById("modal") // this will let react-dom know that we want to render this modal outside the current React tree
  );
}
